import React, { useState } from "react";
import styled from "styled-components";
import { registerUser } from "../services/api";

const Section = styled.section`
  padding: 100px 0;

  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 20px;
  color: #1a5f7a;

  @media (max-width: 576px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 60px;
`;

const FormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 1rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 1rem;
`;

const PhoneInput = styled.div`
  display: flex;
  gap: 10px;
`;

const CountryCodeSelect = styled.select`
  width: 30%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 1rem;
`;

const PhoneNumberInput = styled.input`
  width: 70%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 1rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background-color: #1a5f7a;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #159895;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ThankYouMessage = styled.div`
  text-align: center;
  margin-top: 20px;
  padding: 15px;
  color: #155724;
  background-color: #d4edda;
  border-radius: 5px;
  display: ${(props) => (props.show ? "block" : "none")};
`;

const ErrorMessage = styled.div`
  text-align: center;
  margin-top: 20px;
  padding: 15px;
  color: #721c24;
  background-color: #f8d7da;
  border-radius: 5px;
  display: ${(props) => (props.show ? "block" : "none")};
`;

const JoinForm = () => {
  const [formData, setFormData] = useState({
    fullname: "",
    age: "",
    email: "",
    country: "",
    city: "",
    countryCode: "+92",
    phone: "",
  });

  const [showThankYou, setShowThankYou] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.fullname ||
      !formData.age ||
      !formData.country ||
      !formData.city ||
      !formData.phone
    ) {
      setErrorMessage("Please fill all required fields");
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      return;
    }

    // Validate phone number - must contain only digits
    if (!/^\d+$/.test(formData.phone)) {
      setErrorMessage("Phone number must contain only digits");
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      return;
    }

    try {
      setIsSubmitting(true);

      // Call API to register user
      await registerUser(formData);

      // Show thank you message and reset form
      setShowThankYou(true);
      setFormData({
        fullname: "",
        age: "",
        email: "",
        country: "",
        city: "",
        countryCode: "+92",
        phone: "",
      });

      // Hide thank you message after 5 seconds
      setTimeout(() => setShowThankYou(false), 5000);
    } catch (error) {
      console.error("Registration error:", error);
      setErrorMessage(
        "There was an error submitting your form. Please try again."
      );
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section id="join">
      <Container>
        <SectionTitle>Join Skyline Terrace Community</SectionTitle>
        <SectionSubtitle>
          Fill out the form below and be a part of our growing community
        </SectionSubtitle>

        <FormContainer>
          <form onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="fullname">Full Name *</Label>
              <Input
                type="text"
                id="fullname"
                name="fullname"
                value={formData.fullname}
                onChange={handleChange}
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="age">Age *</Label>
              <Input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleChange}
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="email">Email (optional)</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="e.g. yourname@example.com"
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="country">Country *</Label>
              <Select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
              >
                <option value="">--Select Country--</option>
                <option value="Pakistan">🇵🇰 Pakistan</option>
                <option value="United States">🇺🇸 United States</option>
                <option value="United Kingdom">🇬🇧 United Kingdom</option>
                <option value="United Arab Emirates">
                  🇦🇪 United Arab Emirates
                </option>
                <option value="India">🇮🇳 India</option>
                <option value="China">🇨🇳 China</option>
                <option value="Australia">🇦🇺 Australia</option>
                <option value="Canada">🇨🇦 Canada</option>
                <option value="Germany">🇩🇪 Germany</option>
                <option value="France">🇫🇷 France</option>
                <option value="Italy">🇮🇹 Italy</option>
                <option value="Japan">🇯🇵 Japan</option>
                <option value="Saudi Arabia">🇸🇦 Saudi Arabia</option>
                <option value="South Africa">🇿🇦 South Africa</option>
                <option value="Turkey">🇹🇷 Turkey</option>
              </Select>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="city">City *</Label>
              <Input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter your city"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="phone">Phone Number *</Label>
              <PhoneInput>
                <CountryCodeSelect
                  id="countryCode"
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                >
                  <option value="+92">🇵🇰 +92 (Pakistan)</option>
                  <option value="+1">🇺🇸 +1 (USA)</option>
                  <option value="+44">🇬🇧 +44 (UK)</option>
                  <option value="+971">🇦🇪 +971 (UAE)</option>
                  <option value="+91">🇮🇳 +91 (India)</option>
                  <option value="+86">🇨🇳 +86 (China)</option>
                  <option value="+61">🇦🇺 +61 (Australia)</option>
                  <option value="+1">🇨🇦 +1 (Canada)</option>
                  <option value="+49">🇩🇪 +49 (Germany)</option>
                  <option value="+33">🇫🇷 +33 (France)</option>
                </CountryCodeSelect>
                <PhoneNumberInput
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  pattern="[0-9]*"
                  placeholder="Phone number (digits only)"
                  required
                />
              </PhoneInput>
            </InputGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </SubmitButton>
          </form>

          <ThankYouMessage show={showThankYou}>
            Thank you for joining the Skyline Terrace community! We'll contact
            you soon.
          </ThankYouMessage>

          <ErrorMessage show={showError}>{errorMessage}</ErrorMessage>
        </FormContainer>
      </Container>
    </Section>
  );
};

export default JoinForm;
